package com.narbase.letsgo.web.views.voiceassistant

import com.narbase.kunafa.core.components.TextInput
import com.narbase.kunafa.core.components.View
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.components.style
import com.narbase.kunafa.core.components.textView
import com.narbase.kunafa.core.components.horizontalLayout
import com.narbase.kunafa.core.components.verticalLayout
import com.narbase.kunafa.core.css.Alignment
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.css.marginTop
import com.narbase.kunafa.core.css.alignSelf
import com.narbase.kunafa.core.css.alignItems
import com.narbase.kunafa.core.css.classRuleSet
import com.narbase.kunafa.core.dimensions.dependent.weightOf
import com.narbase.kunafa.core.css.color
import com.narbase.kunafa.core.css.width
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.lifecycle.Observable
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantModelProvider
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantModelRole
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.BasicUiState
import com.narbase.letsgo.web.utils.views.customViews.customCheckBox
import com.narbase.letsgo.web.utils.views.setupLabeledDropDownList
import com.narbase.letsgo.web.utils.views.withLoadingAndError

class VoiceAssistantModelEndpointFields(
    val role: VoiceAssistantModelRole,
    private val roleName: String,
    var provider: VoiceAssistantModelProvider,
    private var baseUrl: String,
    private var model: String,
    val hasStoredApiKey: Boolean,
    private val temperature: Double,
    var isThinkingEnabled: Boolean?,
    var answersInSeparateStep: Boolean?,
    private val defaultModel: String,
    private val loadListedModels: (VoiceAssistantModelEndpointFields) -> Unit,
    private val isTuningOffered: Boolean = true,
) {
    val listedModelsUiState = Observable<BasicUiState>()
    private var listedModels = emptyArray<String>()
    private var listedModelsFailure: String? = null

    private var fieldsContainer: LinearLayout? = null
    private var listedModelsView: LinearLayout? = null
    private var baseUrlInput: TextInput? = null
    private var modelInput: TextInput? = null
    private var apiKeyInput: TextInput? = null
    private var temperatureInput: TextInput? = null

    val enteredBaseUrl get() = baseUrlInput?.text?.trim() ?: baseUrl
    val enteredModel get() = modelInput?.text?.trim()?.takeIf { it.isNotBlank() } ?: model
    val enteredApiKey get() = apiKeyInput?.text?.trim().orEmpty()
    val enteredTemperature get() = temperatureInput?.text?.trim()?.toDoubleOrNull() ?: temperature

    fun showListedModels(models: Array<String>, failure: String?) {
        listedModels = models
        listedModelsFailure = failure
    }

    fun View.render() {
        setupLabeledDropDownList(
            name = "$roleName provider".localized(),
            list = VoiceAssistantModelProvider.values(),
            itemToString = { it.displayName() },
            defaultValue = provider,
            onItemSelected = { selected ->
                if (selected != null && selected != provider) {
                    baseUrl = enteredBaseUrl
                    model = ""
                    provider = selected
                    renderProviderFields()
                }
            },
        )
        fieldsContainer = verticalLayout {
            style {
                width = matchParent
                this["gap"] = "12px"
            }
        }
        renderProviderFields()
    }

    private fun renderProviderFields() {
        fieldsContainer?.apply {
            clearAllChildren()
            baseUrlInput = null
            modelInput = null
            listedModelsView = null
            temperatureInput = null
            when (provider) {
                VoiceAssistantModelProvider.OPENAI -> {
                    apiKeyField(onChanged = { requestListedModels() })
                    listedModelsSection()
                }

                VoiceAssistantModelProvider.OPENAI_COMPATIBLE -> {
                    baseUrlInput = theme.labeledTextInput(this, "$roleName base url".localized()) {
                        text = baseUrl
                        placeholder = "http://127.0.0.1:8000/v1"
                        onChange = { requestListedModels() }
                    }
                    apiKeyField(onChanged = { requestListedModels() })
                    listedModelsSection()
                    if (isTuningOffered) tuningRow()
                }
            }
            requestListedModels()
        }
    }

    private fun LinearLayout.listedModelsSection() {
        verticalLayout {
            style { width = matchParent }
            listedModelsView = verticalLayout {
                style {
                    width = matchParent
                    this["gap"] = "4px"
                }
            }
            listedModelsUiState.clearObservers()
            withLoadingAndError(
                uiState = listedModelsUiState,
                onRetryClicked = { requestListedModels() },
                onLoaded = { renderListedModels() },
            )
            theme.smallSecondaryButton(this) {
                text = "Reload models".localized()
                style {
                    marginTop = 6.px
                    alignSelf = Alignment.Start
                }
                onClick = { requestListedModels() }
            }
        }
    }

    private fun LinearLayout.tuningRow() {
        horizontalLayout {
            style {
                width = matchParent
                alignItems = Alignment.End
                this["gap"] = "16px"
            }
            temperatureInput = theme.labeledTextInput(
                this, "$roleName temperature".localized()
            ) {
                text = temperature.toString()
            }
            isThinkingEnabled?.let { isSelected ->
                customCheckBox("Thinking".localized(), isSelected = isSelected) { isThinkingEnabled = it }
            }
            answersInSeparateStep?.let { isSelected ->
                customCheckBox("Answer in a separate step".localized(), isSelected = isSelected) {
                    answersInSeparateStep = it
                }
            }
        }
    }

    private fun requestListedModels() {
        if (provider == VoiceAssistantModelProvider.OPENAI_COMPATIBLE && enteredBaseUrl.isBlank()) return
        loadListedModels(this)
    }

    private fun renderListedModels() {
        listedModelsView?.apply {
            clearAllChildren()
            modelInput = null
            listedModelsFailure?.let { failure ->
                textView {
                    text = "Could not list models".localized() + ": " + failure
                    style {
                        width = matchParent
                        color = AppColors.redDark
                        this["word-break"] = "break-word"
                    }
                }
            }
            if (listedModels.isEmpty()) {
                modelInput = theme.labeledTextInput(this, "$roleName model".localized()) { text = model }
                return
            }
            if (model.isBlank()) model = defaultModel.takeIf { it in listedModels } ?: listedModels.first()
            modelDropDown(listedModels)
        }
    }

    private fun LinearLayout.apiKeyField(onChanged: (() -> Unit)? = null) {
        apiKeyInput = theme.labeledTextInput(this, "$roleName api key".localized()) {
            element.type = "password"
            element.autocomplete = "new-password"
            placeholder = if (hasStoredApiKey) "Saved".localized() else ""
            onChanged?.let { onChange = { it() } }
        }
    }

    private fun LinearLayout.modelDropDown(models: Array<String>) {
        setupLabeledDropDownList(
            name = "$roleName model".localized(),
            list = if (model in models) models else arrayOf(model) + models,
            itemToString = { it },
            defaultValue = model,
            onItemSelected = { selected -> selected?.let { model = it } },
        )
    }

    private fun VoiceAssistantModelProvider.displayName() = when (this) {
        VoiceAssistantModelProvider.OPENAI -> "OpenAI"
        VoiceAssistantModelProvider.OPENAI_COMPATIBLE -> "OpenAI compatible api".localized()
    }
}
